import { Component } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { Col, Row } from 'react-bootstrap'
import classNames from 'classnames'
import Image from 'components/image'
import pluralize from 'pluralize'
import { track, tracking } from 'lib/analytics'

import { STORE_TITLE_TEMPLATE } from 'store/constants'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import * as BLConstants from 'shared/constants'
import Link from 'components/link'
import { RegistryBanner } from 'baby-ui/compounds/RegistryBanner/RegistryBanner'
import { RegistryBannerVariant } from 'baby-ui/lib/RegistryBannerVariant'
import BabylistButtonBanner from '../landing-page/babylist-button-banner'
import ProductGrid from '../product-grid'
import css from './search-page.scss'
import { SectionHeading } from '../../../components/headings'
import { ProductFilters } from '../product-filters'

const BrandPageSuggestion = ({ brand }) => {
  if (brand && brand.brandPageUrl) {
    return (
      <div className="h6 mtm">
        <Link to={brand.brandPageUrl}>Shop all {brand.title} products</Link>
      </div>
    )
  }
  return null
}

BrandPageSuggestion.propTypes = {
  brand: PropTypes.shape({
    brandPageUrl: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
}

export const NoResults = ({ searchTerm }) => {
  const [currentUser] = useCurrentUser()
  return (
    <>
      <p className="h6 text-center mvxl pvxl text-muted">
        {`Sorry, there are no products matching '${searchTerm.toUpperCase()}'.`}
      </p>
      {!!currentUser && <BabylistButtonBanner />}
    </>
  )
}

NoResults.propTypes = { searchTerm: PropTypes.string.isRequired }

const MobileCallout = () => (
  <div className="text-center h6 pal gray-bg visible-xs visible-sm">
    <a href={BLConstants.FIREBASE_HOME_URL}>
      <Image
        className={classNames(css.appIcon, 'mbm')}
        height={74}
        role="presentation"
        src="//images.babylist.com/image/upload/f_auto,q_auto/v1559328348/iTunesArtwork_2x_fapv5g.png"
        width={74}
      />
    </a>
    <p className="text-bold mbn">Don't see what you're looking for?</p>
    <p className="mbn">
      {`You can add from any store with the 5-star `}
      <a href={BLConstants.FIREBASE_HOME_URL}>Babylist app.</a>
    </p>
  </div>
)

export class BaseSearchPage extends Component {
  fireBreastPumpBannerViewedEvent = () => {
    const { searchTerm } = this.props
    track.healthBannerViewed({
      eventCta: 'Check Your Eligibility',
      eventLocation: track.EventLocation.STORE_MINUS_SEARCH,
      searchTerm,
    })
  }

  fireBreastPumpBannerClickedEvent = () => {
    const { searchTerm } = this.props
    track.healthBannerClicked({
      eventCta: 'Check Your Eligibility',
      eventLocation: track.EventLocation.STORE_MINUS_SEARCH,
      searchTerm,
    })
  }

  render() {
    const { heroProducts, productCards, searchTerm, brand } = this.props
    const productCount = productCards?.length

    return (
      <div>
        <Helmet
          meta={[
            {
              name: 'robots',
              content: 'noindex',
            },
          ]}
          title={searchTerm || 'Search'}
          titleTemplate={STORE_TITLE_TEMPLATE}
        />
        <RegistryBanner
          query={searchTerm}
          variant={RegistryBannerVariant.SearchResultBreastPumpBanner}
          onClick={this.fireBreastPumpBannerClickedEvent}
          onView={this.fireBreastPumpBannerViewedEvent}
        />
        <Row>
          <Col sm={8}>
            <div className={css.titleWrapper}>
              <SectionHeading className={css.title}>
                {`'${searchTerm}'`}
              </SectionHeading>
              {productCount > 0 && (
                <div className={css.productCount}>
                  {`${productCount} ${pluralize('product', productCount)}`}
                </div>
              )}
            </div>
            <BrandPageSuggestion brand={brand} />
          </Col>
        </Row>
        <hr className="mts mbm" />
        {productCount > 0 && <ProductFilters products={heroProducts} />}
        {productCount === 0 ? (
          <NoResults searchTerm={searchTerm} />
        ) : (
          <ProductGrid productCards={productCards} />
        )}
        <MobileCallout />
      </div>
    )
  }
}

BaseSearchPage.propTypes = {
  heroProducts: PropTypes.array,
  productCards: PropTypes.array,
  searchTerm: PropTypes.string.isRequired,
  brand: PropTypes.shape({
    brandPageUrl: PropTypes.string,
    title: PropTypes.string,
  }),
  featureFlags: PropTypes.shape({
    show_search_results_banner: PropTypes.bool,
  }),
}

BaseSearchPage.defaultProps = {
  heroProducts: null,
  productCards: null,
  brand: null,
  featureFlags: null,
}
